import React from 'react'
import { useParams } from 'react-router-dom'
import { useSiteData } from '../context/SiteDataContext.jsx'
import { excerptFromHtml, buildResponsiveImageProps } from '../lib/cms.js'
import { ButtonLink, Card, Container, SectionHeading } from '../components/atoms.jsx'
import { PageHero, RichContent, SeoManager, RelatedStories } from '../components/content.jsx'
import { ArrowLeft, Phone, Mail, MapPin, CheckCircle } from 'lucide-react'
import { NotFoundPage } from './NotFoundPage.jsx'

export function PatientStoryPage() {
  const { slug } = useParams()
  const { patienthistorier } = useSiteData()
  const story = patienthistorier.find(
    (entry) => entry?.slug?.da === slug || entry?.slug === slug
  )

  if (!story) return <NotFoundPage />

  const femaleNames = [
    'karen', 'dyveke', 'bodil', 'joan', 'pernille', 'charlotte',
    'zivile', 'doris', 'astrid', 'birthe', 'stine', 'caroline',
    'heidi', 'aase', 'zunja', 'tanja', 'iben', 'bettina', 'dorte'
  ]
  const isFemale = femaleNames.some((name) =>
    (story.title || '').toLowerCase().includes(name)
  )
  const fallbackImage = isFemale
    ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/woman-avatar-copy.jpg'
    : 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/man-avatar-copy.jpg'
  const imageUrl = story.image?.[0] || fallbackImage

  return (
    <>
      <SeoManager
        title={`${story.title} – ${story.summary || 'Patienthistorie'} | Helseklinikken`}
        description={story.summary || excerptFromHtml(story.content, 180)}
        image={story.image?.[0] || 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/patienthistorier.jpg'}
        canonical={`https://helselarsen.dk/patienthistorie/${slug}/`}
      />

      <main>
        {/* Hero Header */}
        <section 
          className="border border-forest-900 bg-forest-950 py-16 sm:py-24 lg:py-28 min-h-[300px] flex items-center"
          style={{ 
            backgroundImage: 'linear-gradient(180deg, rgba(16, 32, 22, 0.2) 0%, rgba(16, 32, 22, 0.2) 100%), url("https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/patienthistorier.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Container>
            <PageHero
              page={story}
              kicker="Patienthistorie"
              description={story.summary || excerptFromHtml(story.content, 160)}
              actions={[
                { label: 'Tilbage til alle historier', href: '/patienthistorier' },
                { label: 'Kontakt os', href: '/kontakt', variant: 'secondary' },
              ]}
            />
          </Container>
        </section>

        {/* Main Content Area */}
        <section className="py-12 lg:py-16 bg-white">
          <Container>
            <div className="mb-6">
              <ButtonLink
                href="/patienthistorier"
                variant="ghost"
                className="inline-flex items-center gap-2 text-sm font-semibold text-forest-800 hover:text-forest-950 pl-0"
              >
                <ArrowLeft className="h-4 w-4" /> Tilbage til oversigten over patienthistorier
              </ButtonLink>
            </div>

            <div className="grid gap-10 lg:grid-cols-3 items-start">
              {/* Left/Main Column: The Story */}
              <div className="lg:col-span-2 space-y-8">
                <Card className="space-y-6 p-6 sm:p-10 border border-stone-200 bg-white shadow-xs rounded-xl">
                  <div className="border-b border-stone-100 pb-5 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-forest-700">
                      Patientberetning
                    </p>
                    <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-forest-950">
                      {story.title}
                    </h1>
                    {story.summary && (
                      <p className="text-base sm:text-lg font-semibold text-forest-800">
                        {story.summary}
                      </p>
                    )}
                  </div>

                  <RichContent
                    html={story.content || ''}
                    className="space-y-5 text-base leading-8 text-stone-700 [&_a]:font-semibold [&_a]:text-forest-800 [&_a]:underline [&_h1]:font-serif [&_h1]:text-3xl [&_h1]:text-forest-900 [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:text-forest-900 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:text-forest-900 [&_p]:leading-8 [&_blockquote]:border-l-4 [&_blockquote]:border-forest-600 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-stone-700"
                  />
                </Card>
              </div>

              {/* Right Column: Profile & Info */}
              <div className="space-y-6">
                <Card className="p-6 border border-stone-200 bg-stone-50/70 rounded-xl space-y-4 text-center">
                  <img
                    src={buildResponsiveImageProps(imageUrl, [300, 600]).src}
                    srcSet={buildResponsiveImageProps(imageUrl, [300, 600]).srcSet}
                    sizes="128px"
                    alt={story.title}
                    className="h-32 w-32 mx-auto rounded-full object-cover border-4 border-white shadow-sm bg-white"
                  />
                  <div>
                    <h3 className="font-serif text-lg font-bold text-forest-950">{story.title}</h3>
                    {story.summary && (
                      <p className="text-xs font-semibold text-forest-800 mt-1">{story.summary}</p>
                    )}
                  </div>
                </Card>

                {/* Quick Info Box */}
                <div className="bg-forest-950 text-sand-100 p-6 rounded-xl space-y-3">
                  <h4 className="font-serif text-base font-bold text-white">Har du lignende symptomer?</h4>
                  <p className="text-xs text-stone-300 leading-relaxed">
                    Vi har mere end 40 års erfaring med at hjælpe patienter med akutte og kroniske lidelser. Ring og få en uforpligtende snak.
                  </p>
                  <a
                    href="tel:86384117"
                    className="inline-flex items-center gap-2 text-sm font-bold text-sand-200 hover:text-white pt-1"
                  >
                    <Phone className="h-4 w-4 text-forest-400" /> (+45) 86 38 41 17
                  </a>
                </div>
              </div>
            </div>

            {/* Related Stories */}
            <div className="mt-16">
              <RelatedStories slug={story.terms?.treatment?.[0] || ''} stories={patienthistorier} />
            </div>
          </Container>
        </section>
      </main>
    </>
  )
}
